import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label
} from 'recharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { zhTW } from 'date-fns/locale';
import { subDays } from 'date-fns';
import { getDailyReaderStats } from '../services/readerService';

const colorMap = {
  ETG小客車: '#CE93D8',
  LEON大車: '#FFAB91',
  ETG大型車輛: '#A5D6A7'
};

const DailyReaderTrafficChart = ({ readerData, date, handleDateChange, dataFetcher, minDate: minDateProp }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedReader, setSelectedReader] = useState(
    readerData && readerData.length > 0 ? readerData[0].leonReaderMACAddress : ''
  );

  // readerData 載入後同步預設選擇
  useEffect(() => {
    if (readerData && readerData.length > 0 && !selectedReader) {
      setSelectedReader(readerData[0].leonReaderMACAddress);
    }
  }, [readerData]);

  const [visibleReaders, setVisibleReaders] = useState({
    ETG小客車: true,
    LEON大車: true,
    ETG大型車輛: true
  });
  const fetchData = async () => {
    if (!selectedReader) return; // 尚未選到讀取器時不發請求
    setLoading(true);
    try {
      const fetcher = dataFetcher || getDailyReaderStats;
      const stats = await fetcher(selectedReader, date);
      const formattedData = stats.map((item) => ({
        time: item.timeTag,
        ...item.readers
      }));
      setChartData(formattedData);
    } catch (error) {
      console.error('獲取每日車輛數據時發生錯誤:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [date, selectedReader]);

  const handleReaderChange = (event) => {
    setSelectedReader(event.target.value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: 'white',
            padding: '10px',
            border: '1px solid #ccc',
            position: 'relative',
            top: '-10px',
            left: '10px'
          }}
        >
          <p>{`時間: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.value} 輛`}
            </p>
          ))}
          <p style={{ fontWeight: 'bold' }}>
            {`總流量: ${payload.reduce((sum, entry) => sum + entry.value, 0)} 輛`}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', mt: 8 }}>
      {payload.map((entry, index) => (
        <Box
          key={`item-${index}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            m: 0.5,
            cursor: 'pointer',
            position: 'relative'
          }}
          onClick={() => toggleReader(entry.dataKey)}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: entry.color,
              mr: 0.5
            }}
          />
          <Typography
            variant="body2"
            sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                borderTop: visibleReaders[entry.dataKey] ? 'none' : '1px solid black'
              }
            }}
          >
            {entry.dataKey}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const toggleReader = (reader) => {
    setVisibleReaders((prev) => ({
      ...prev,
      [reader]: !prev[reader]
    }));
  };

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>選擇讀取器</InputLabel>
          <Select
            value={selectedReader}
            onChange={handleReaderChange}
            label="選擇讀取器"
            size="small"
          >
            {readerData.map((reader) => (
              <MenuItem key={reader.leonReaderMACAddress} value={reader.leonReaderMACAddress}>
                {reader.leonReaderName || reader.address}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
          <DatePicker
            label="選擇日期"
            value={date ? new Date(date) : null}
            onChange={(newDate) => newDate && handleDateChange(newDate)}
            minDate={minDateProp !== undefined ? minDateProp : subDays(new Date(), 90)}
            maxDate={new Date()}
            renderInput={(params) => <TextField {...params} variant="standard" />}
          />
        </LocalizationProvider>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 450 }}>
          <CircularProgress size={48} />
          <Typography sx={{ ml: 2 }} color="text.secondary">資料載入中...</Typography>
        </Box>
      )}
      {!loading && <ResponsiveContainer width="100%" height={450}>
        <BarChart
          barSize={30}
          barGap={2}
          data={chartData}
          margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" angle={-45} textAnchor="end" interval={0} dy={10}>
            <Label value="時間(小時)" position="insideBottom" offset={-60} />
          </XAxis>
          <YAxis>
            <Label value="車流量(輛)" angle={-90} position="insideLeft" dx={-10} />
          </YAxis>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
          {Object.keys(colorMap).map((type) => (
            <Bar
              key={type}
              dataKey={type}
              stackId="a"
              fill={colorMap[type]}
              name={type}
              hide={!visibleReaders[type]}
              fillOpacity={1}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>}
    </Paper>
  );
};

export default DailyReaderTrafficChart;
